import { useState } from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonContent,
  IonSelect,
  IonSelectOption,
  IonText,
  IonSpinner,
  useIonToast,
} from '@ionic/react';
import { closeOutline } from 'ionicons/icons';
import { useMutation } from '@apollo/client/react';
import { REPORT_USER_CONTENT } from '../../graphql/mutations/reportUserContent';
import { UserContentReportReasonEnum } from '../../types/graphql-generated';
import type {
  ReportUserContentMutation,
  ReportUserContentMutationVariables,
  UserContentTypeEnum,
} from '../../types/graphql-generated';

const REPORT_REASON_OPTIONS: { value: UserContentReportReasonEnum; label: string }[] = [
  { value: UserContentReportReasonEnum.Spam, label: 'Spam o publicidad' },
  { value: UserContentReportReasonEnum.Harassment, label: 'Acoso o insultos' },
  { value: UserContentReportReasonEnum.HateSpeech, label: 'Discurso de odio' },
  { value: UserContentReportReasonEnum.Violence, label: 'Violencia o amenazas' },
  { value: UserContentReportReasonEnum.SexualContent, label: 'Contenido sexual' },
  { value: UserContentReportReasonEnum.Misinformation, label: 'Información falsa' },
  { value: UserContentReportReasonEnum.Other, label: 'Otro motivo' },
];

interface ReportContentModalProps {
  isOpen: boolean;
  onClose: () => void;
  contentType: UserContentTypeEnum;
  contentId: string;
}

/** Lets the user report a content (e.g. a league message) as inappropriate. */
export const ReportContentModal = ({
  isOpen,
  onClose,
  contentType,
  contentId,
}: ReportContentModalProps) => {
  const [reason, setReason] = useState<UserContentReportReasonEnum | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [presentToast] = useIonToast();

  const [reportUserContent, { loading }] = useMutation<
    ReportUserContentMutation,
    ReportUserContentMutationVariables
  >(REPORT_USER_CONTENT);

  const handleClose = () => {
    setReason(null);
    setErrorMessage(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!reason) return;
    setErrorMessage(null);

    try {
      const { data } = await reportUserContent({
        variables: { input: { contentType, contentId, reason } },
      });

      const result = data?.reportUserContent;
      if (result?.success) {
        presentToast({
          message: 'Gracias. Hemos recibido tu reporte y lo revisaremos.',
          duration: 3000,
          position: 'bottom',
          positionAnchor: 'app-footer',
          cssClass: 'toast-success',
          buttons: [{ icon: closeOutline, role: 'cancel' }],
        });
        handleClose();
      } else {
        setErrorMessage(result?.errors?.[0] ?? 'No se ha podido enviar el reporte.');
      }
    } catch (error) {
      console.error('Error reporting content:', error);
      setErrorMessage('No se ha podido enviar el reporte. Inténtalo de nuevo más tarde.');
    }
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={handleClose}>
      <IonHeader>
        <IonToolbar>
          <IonTitle className="ml-4">Reportar contenido</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={handleClose}>Cancelar</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <IonText>
          <p className="text-sm text-gray-600 mb-4">
            ¿Crees que este contenido es inadecuado? Indícanos el motivo y lo revisaremos.
          </p>
        </IonText>

        <IonSelect
          label="Motivo"
          labelPlacement="floating"
          fill="outline"
          interface="popover"
          placeholder="Selecciona un motivo"
          value={reason}
          onIonChange={(e) => setReason(e.detail.value as UserContentReportReasonEnum)}
        >
          {REPORT_REASON_OPTIONS.map((option) => (
            <IonSelectOption key={option.value} value={option.value}>
              {option.label}
            </IonSelectOption>
          ))}
        </IonSelect>

        {errorMessage && (
          <IonText color="danger">
            <p className="text-sm mt-3">{errorMessage}</p>
          </IonText>
        )}

        <div className="mt-6">
          <IonButton
            expand="block"
            color="danger"
            onClick={handleSubmit}
            disabled={loading || !reason}
          >
            {loading ? <IonSpinner name="crescent" /> : 'Reportar'}
          </IonButton>
        </div>
      </IonContent>
    </IonModal>
  );
};
